import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useSearch } from '@tanstack/react-router';
import { useEffect, useState, type ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ButtonLink } from '../../../components/ButtonLink';
import { useRequestOnboardingLinkMutation } from '../../../lib/auth';

const RESEND_SECONDS = 54;

export function RegisterCompanyCheckEmailPage(): ReactElement {
    const { t } = useTranslation();
    const { email } = useSearch({ from: '/split-hero-layout/register-company/check-email' });
    const requestLink = useRequestOnboardingLinkMutation();
    const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);

    useEffect((): (() => void) | undefined => {
        if (secondsLeft <= 0) {
            return undefined;
        }
        const timer = window.setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
        return () => window.clearTimeout(timer);
    }, [secondsLeft]);

    return (
        <Stack spacing={3}>
            <Typography variant="h5" component="h1">
                {t('ONBOARDING.REGISTER.CHECK_EMAIL.TITLE')}
            </Typography>

            <Stack spacing={2}>
                <Alert severity="info">
                    <AlertTitle>{t('ONBOARDING.REGISTER.CHECK_EMAIL.LINK_SENT')}</AlertTitle>
                    <Trans
                        i18nKey="ONBOARDING.REGISTER.CHECK_EMAIL.LINK_BODY"
                        values={{ email }}
                        components={{ strong: <strong /> }}
                    />
                </Alert>

                <Stack spacing={1.5}>
                    <ButtonLink variant="outlined" color="primary" fullWidth to="/register-company">
                        {t('ONBOARDING.REGISTER.CHECK_EMAIL.USE_DIFFERENT_EMAIL')}
                    </ButtonLink>
                    <Typography variant="caption" color="text.secondary">
                        {secondsLeft > 0 ? (
                            t('ONBOARDING.REGISTER.CHECK_EMAIL.RESEND_IN', { seconds: secondsLeft })
                        ) : (
                            <Trans
                                i18nKey="ONBOARDING.REGISTER.CHECK_EMAIL.RESEND_AVAILABLE"
                                components={{
                                    resendLink: (
                                        <Link
                                            component="button"
                                            type="button"
                                            onClick={() => {
                                                setSecondsLeft(RESEND_SECONDS);
                                                requestLink.mutate({ email, tosAccepted: true });
                                            }}
                                        />
                                    ),
                                }}
                            />
                        )}
                    </Typography>
                </Stack>
            </Stack>
        </Stack>
    );
}
